import React from 'react';
import { Link } from 'react-router-dom';
import { FiTarget, FiUsers, FiHeart, FiZap, FiLinkedin, FiGithub, FiMail } from 'react-icons/fi';
import PublicPageHeader from '../../components/common/PublicPageHeader';
import PublicPageFooter from '../../components/common/PublicPageFooter';

const About = () => {
  const values = [
    {
      icon: FiTarget,
      title: 'Mission-Driven',
      description: 'We build tools that empower teams to work smarter, not harder. Every feature serves a purpose.',
      color: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
    },
    {
      icon: FiUsers,
      title: 'User-Focused',
      description: 'Every decision starts with understanding our users. Your feedback shapes our product.',
      color: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400'
    },
    {
      icon: FiHeart,
      title: 'Passion for Excellence',
      description: 'We believe in creating software that people genuinely love to use every day.',
      color: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'
    },
    {
      icon: FiZap,
      title: 'Innovation First',
      description: 'We leverage AI and modern technology to continuously improve and adapt to changing needs.',
      color: 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
    }
  ];

  const team = [
    {
      name: 'Shobha Sharma',
      role: 'Chief Executive Officer',
      bio: 'Visionary leader driving the strategic direction of Tasklina. Passionate about building products that transform how teams collaborate.',
      image: '/shobha_sharma.png',
      social: {
        linkedin: 'https://linkedin.com/in/shobha-sharma-npl',
        email: 'shobha.sharma@nexuspointluxe.com'
      }
    },
    {
      name: 'Kundan Kumar',
      role: 'Chief Technology Officer',
      bio: 'Full-stack engineer and architect behind Tasklina\'s technical foundation. Specializes in building scalable, user-friendly applications.',
      image: '/kundan_Pic.png',
      social: {
        linkedin: 'https://linkedin.com/in/itskundankumar',
        github: 'https://github.com/itskundan-01',
        email: 'kundan.kumar@nexuspointluxe.com'
      }
    }
  ];

  const stats = [
    { value: '2025', label: 'Founded' },
    { value: 'AI-Powered', label: 'Technology' },
    { value: '99.9%', label: 'Uptime Target' },
    { value: 'Global', label: 'Reach' }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white dark:from-gray-800 dark:to-gray-900 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              Building the Future of <span className="text-blue-600">Task Management</span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
              Tasklina was born from a simple idea: task management should be intuitive, 
              powerful, and accessible to everyone. We're on a mission to help teams 
              achieve more with less effort.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-blue-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-500 dark:text-gray-400 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-600 dark:text-gray-300 leading-relaxed">
                <p>
                  Tasklina started in 2025 with a vision to revolutionize how modern teams 
                  manage their work. We noticed that existing tools were either too complex 
                  for small teams or too simple for growing organizations.
                </p>
                <p>
                  Our platform combines the simplicity of a to-do list with the power of 
                  enterprise project management, enhanced by AI-driven insights that help 
                  you work smarter, not harder.
                </p>
                <p>
                  Today, we're building a comprehensive task management ecosystem that 
                  adapts to your workflow, whether you're an individual professional or 
                  part of a large enterprise team.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
              <p className="text-blue-100 leading-relaxed mb-6">
                To empower every team and individual to achieve their goals through 
                intuitive, AI-powered task management that removes friction and 
                amplifies productivity.
              </p>
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                  <FiTarget className="w-6 h-6" />
                </div>
                <div>
                  <div className="font-semibold">Focus on what matters</div>
                  <div className="text-blue-200 text-sm">Let us handle the rest</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Our Values</h2>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              These principles guide everything we do at Tasklina
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100 dark:border-gray-700"
                >
                  <div className={`w-12 h-12 rounded-xl ${value.color} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Meet Our Team</h2>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              The passionate people behind Tasklina
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {team.map((member, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-lg transition-all overflow-hidden group"
              >
                <div className="p-8">
                  <div className="flex items-start space-x-6">
                    <div className="flex-shrink-0">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-24 h-24 rounded-2xl object-cover bg-gray-100"
                        onError={(e) => {
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&size=96&background=3b82f6&color=fff`;
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
                        {member.name}
                      </h3>
                      <p className="text-blue-600 font-medium text-sm mb-3">
                        {member.role}
                      </p>
                      <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                        {member.bio}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 mt-6 pt-6 border-t border-gray-100 dark:border-gray-700">
                    {member.social.linkedin && (
                      <a
                        href={member.social.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                      >
                        <FiLinkedin className="w-5 h-5" />
                      </a>
                    )}
                    {member.social.github && (
                      <a
                        href={member.social.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <FiGithub className="w-5 h-5" />
                      </a>
                    )}
                    {member.social.email && (
                      <a
                        href={`mailto:${member.social.email}`}
                        className="p-2 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                      >
                        <FiMail className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Transform Your Workflow?
          </h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of teams who are already using Tasklina to manage their work more effectively.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/auth/register-options"
              className="px-8 py-3 bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 rounded-full font-semibold hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors shadow-lg"
            >
              Get Started Free
            </Link>
            <Link
              to="/contact"
              className="px-8 py-3 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-400 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <PublicPageFooter />
    </div>
  );
};

export default About;
